import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const TEAL = '#1A6B54';
const GOLD = '#B8892A';
const INK = '#18140C';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';

const STATUS_LABELS = {
  pending_payment: '待付款',
  paid: '已付款',
  shipping: '运输中',
  delivered: '已送达',
  completed: '已完成',
  cancelled: '已取消',
  refunded: '已退款',
  offer: '议价中',
};
const STATUS_COLORS = {
  pending_payment: '#E67E22',
  paid: '#3498DB',
  shipping: '#9B59B6',
  delivered: TEAL,
  completed: '#27AE60',
  cancelled: '#95A5A6',
  refunded: '#C0392B',
  offer: GOLD,
};

const TABS = [
  { key: '', label: '全部' },
  { key: 'pending_payment', label: '待付款' },
  { key: 'paid', label: '待发货' },
  { key: 'shipping', label: '运输中' },
  { key: 'completed', label: '已完成' },
  { key: 'refunded', label: '退款' },
];

export default function MarketMyOrders() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const role = searchParams.get('role') || 'buyer';
  const statusFilter = searchParams.get('status') || '';
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { role };
    if (statusFilter) params.status = statusFilter;
    api.get('/market/orders/list', { params })
      .then(r => setOrders(r.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [role, statusFilter]);

  const setStatus = (s) => {
    const p = new URLSearchParams(searchParams);
    if (s) p.set('status', s); else p.delete('status');
    setSearchParams(p);
  };

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: 40 }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: '14px 16px',
          background: '#fff', borderBottom: `1px solid ${BD}`,
        }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: INK }}>←</button>
          <span style={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 16, color: INK }}>
            {role === 'seller' ? '我卖出的' : '我买到的'}
          </span>
          <div style={{ width: 30 }} />
        </div>

        {/* Role tabs */}
        <div style={{
          display: 'flex', background: '#fff', borderBottom: `1px solid ${BD}`,
        }}>
          {['buyer', 'seller'].map(r => (
            <button key={r} onClick={() => {
              const p = new URLSearchParams(searchParams);
              p.set('role', r);
              p.delete('status');
              setSearchParams(p);
            }} style={{
              flex: 1, padding: '10px 0', border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: role === r ? 600 : 400,
              color: role === r ? (r === 'buyer' ? TEAL : GOLD) : MUTED,
              background: 'transparent',
              borderBottom: role === r ? `2px solid ${r === 'buyer' ? TEAL : GOLD}` : '2px solid transparent',
            }}>{r === 'buyer' ? '我买的' : '我卖的'}</button>
          ))}
        </div>

        {/* Status tabs */}
        <div style={{
          display: 'flex', overflowX: 'auto', background: '#fff',
          borderBottom: `1px solid ${BD}`, padding: '0 8px',
          scrollbarWidth: 'none',
        }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setStatus(t.key)} style={{
              flexShrink: 0, padding: '10px 14px', border: 'none', cursor: 'pointer',
              fontSize: 12, color: statusFilter === t.key ? TEAL : MUTED,
              background: 'transparent', fontWeight: statusFilter === t.key ? 600 : 400,
              borderBottom: statusFilter === t.key ? `2px solid ${TEAL}` : '2px solid transparent',
            }}>{t.label}</button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
        ) : orders.length === 0 ? (
          <div style={{ padding: 60 }}><Empty description="暂无订单" /></div>
        ) : (
          <div style={{ padding: '12px 16px' }}>
            {orders.map(order => {
              const photos = order.photos || [];
              const thumb = photos[0] || '';
              const statusLabel = STATUS_LABELS[order.status] || order.status;
              const statusColor = STATUS_COLORS[order.status] || '#999';
              return (
                <button key={order.id} onClick={() => navigate(`/market/orders/${order.id}`)} style={{
                  width: '100%', display: 'flex', gap: 12, padding: 14,
                  background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
                  marginBottom: 10, cursor: 'pointer', textAlign: 'left',
                }}>
                  <div style={{
                    width: 72, height: 72, borderRadius: 8, flexShrink: 0,
                    background: thumb ? `url(${thumb}) center/cover` : '#F5F5F5',
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 14, fontWeight: 500, color: INK,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{order.item_name || '—'}</div>
                    <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
                      {order.brand} · {order.order_no}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                      <span style={{ fontSize: 16, fontWeight: 600, color: INK }}>
                        ¥{Number(order.price_cny || 0).toLocaleString()}
                      </span>
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 4,
                        background: `${statusColor}15`, color: statusColor,
                      }}>{statusLabel}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
